import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ASSETS, PATHS } from '../lib/content';
import { CalendarHeart, FileText, MailOpen, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const START_DATES = [
  'Path I — 15 September 2026',
  'Path II — Spring 2027',
  'Path III — Autumn 2027',
  'I’m not yet sure',
];

const STEPS = [
  { n: 1, key: 'intake', icon: CalendarHeart, title: 'Intake Conversation', desc: 'A gentle first meeting to explore fit and readiness.' },
  { n: 2, key: 'apply', icon: FileText, title: 'Application', desc: 'A reflective form, written at your own pace.' },
  { n: 3, key: 'invite', icon: MailOpen, title: 'Invitation to Join', desc: 'Welcome details, start date and considered payment options.' },
];

const emptyApp = {
  full_name: '', email: '', pronouns: '', country_timezone: '',
  path_interest: [], preferred_start: '',
  drawn_to_programme: '', current_relationship: '', hopes: '',
  prior_therapy: '', current_support: '', additional_notes: '',
  consent: false,
  marketing_consent: false,
};

const emptyIntake = {
  full_name: '', email: '', pronouns: '', country_timezone: '', message: '', consent: false, marketing_consent: false,
};

export default function Booking() {
  const [activeTab, setActiveTab] = useState('intake'); // 'intake' | 'apply'
  const [intake, setIntake] = useState(emptyIntake);
  const [intakeSent, setIntakeSent] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(false);

  const [app, setApp] = useState(emptyApp);
  const [appSent, setAppSent] = useState(false);
  const [appBusy, setAppBusy] = useState(false);

  const togglePath = (id) => {
    setApp((s) => ({
      ...s,
      path_interest: s.path_interest.includes(id)
        ? s.path_interest.filter((x) => x !== id)
        : [...s.path_interest, id],
    }));
  };

  const submitIntake = async (e) => {
    e.preventDefault();
    if (!intake.consent) { toast.error('Please tick the consent box to continue.'); return; }
    setIntakeBusy(true);
    try {
      await axios.post(`${API}/intake`, intake);
      setIntakeSent(true);
      toast.success('Thank you. We’ll be in touch with a gentle next step.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please check the form.');
    } finally { setIntakeBusy(false); }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!app.consent) { toast.error('Please tick the consent box to continue.'); return; }
    if (app.path_interest.length === 0) { toast.error('Please select at least one Path.'); return; }
    setAppBusy(true);
    try {
      await axios.post(`${API}/applications`, app);
      setAppSent(true);
      toast.success('Thank you for sharing this with us.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please check the form.');
    } finally { setAppBusy(false); }
  };

  return (
    <div data-testid="page-booking">
      {/* HERO */}
      <section className="pt-28 md:pt-36 pb-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-5">Booking</p>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-brown leading-[1.05]">
            Begin <em className="italic text-brand-brown-light">your</em> journey
          </h1>
          <p className="mt-7 font-sans text-base md:text-lg text-brand-brown-light/90 leading-relaxed">
            Because Nurture the Self is a therapeutic group pathway, all prospective participants are invited to attend a complimentary intake conversation before booking. This allows us to gently explore whether the programme is the right fit for your current needs, and ensures that we create a safe, supportive and appropriately held group environment for everyone involved.
          </p>
          <p className="mt-5 font-sans text-base text-brand-brown-light/90 leading-relaxed max-w-3xl mx-auto">
            We believe that thoughtful preparation is an important part of the therapeutic process itself, and helps establish the foundations for meaningful participation throughout the journey.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`booking-step-${s.n}`} className="bg-brand-bone rounded-3xl p-8 border border-brand-brown/5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-10 w-10 rounded-full bg-brand-sage flex items-center justify-center">
                    <s.icon size={18} className="text-brand-brown" />
                  </div>
                  <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold">Step {s.n}</span>
                </div>
                <h3 className="font-serif text-2xl text-brand-brown mb-2">{s.title}</h3>
                <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-sans text-sm text-brand-brown-muted italic">
            Read → Reflect → Apply → Intake Conversation → Invitation to Join → Considered next steps
          </p>
        </div>
      </section>

      {/* QUIET QUOTE */}
      <section className="py-16 bg-brand-sage">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="divider-leaf mb-8" />
          <p className="font-serif italic text-2xl md:text-3xl text-brand-brown-light leading-relaxed">
            “There is no need to do anything more right now — only to begin where you are.”
          </p>
        </div>
      </section>

      {/* SPACES LIMITED NOTE */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div data-testid="booking-spaces-limited" className="bg-brand-sage rounded-3xl p-7 md:p-8 border border-brand-brown/5">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">Spaces Are Intentionally Limited</p>
            <h3 className="font-serif text-2xl text-brand-brown leading-snug">A small group, carefully held.</h3>
            <p className="mt-3 font-sans text-sm text-brand-brown-light/95 leading-relaxed">
              Each cohort is intentionally limited to between 8 and 12 participants — to create a safe, supportive and carefully held therapeutic group experience. Places are offered following the intake conversation, as we compose each group with care.
            </p>
          </div>
        </div>
      </section>

      {/* FORMS */}
      <section className="py-16 bg-brand-bg-soft">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">A gentle next step</h2>
            <p className="mt-3 font-sans text-base text-brand-brown-light/90 leading-relaxed max-w-2xl">
              You are welcome to begin with a short note requesting a complimentary intake conversation, or, if you are ready, to share more through the application form. There is no rush.
            </p>
          </div>

          <div className="inline-flex bg-brand-bone rounded-full p-1.5 mb-10 border border-brand-brown/5">
            <button
              data-testid="tab-intake"
              onClick={() => setActiveTab('intake')}
              className={`font-sans text-sm tracking-wide px-6 py-2.5 rounded-full transition-colors duration-300 ${activeTab === 'intake' ? 'bg-brand-brown text-brand-bg' : 'text-brand-brown-light hover:text-brand-brown'}`}
            >
              Book a Complimentary Conversation
            </button>
            <button
              data-testid="tab-apply"
              onClick={() => setActiveTab('apply')}
              className={`font-sans text-sm tracking-wide px-6 py-2.5 rounded-full transition-colors duration-300 ${activeTab === 'apply' ? 'bg-brand-brown text-brand-bg' : 'text-brand-brown-light hover:text-brand-brown'}`}
            >
              Apply for Path I
            </button>
          </div>

          {/* INTAKE FORM */}
          {activeTab === 'intake' && (
            <div data-testid="intake-form-wrapper" className="bg-brand-bone rounded-3xl p-8 md:p-12 border border-brand-brown/5">
              {intakeSent ? (
                <SuccessMessage testid="intake-success" />
              ) : (
                <form onSubmit={submitIntake} className="space-y-6">
                  <Field label="Full name" testid="intake-name" required>
                    <input
                      type="text" required value={intake.full_name}
                      onChange={(e) => setIntake({ ...intake, full_name: e.target.value })}
                      className="form-input" data-testid="intake-name-input"
                    />
                  </Field>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field label="Email address" testid="intake-email" required>
                      <input
                        type="email" required value={intake.email}
                        onChange={(e) => setIntake({ ...intake, email: e.target.value })}
                        className="form-input" data-testid="intake-email-input"
                      />
                    </Field>
                    <Field label="Pronouns (optional)" testid="intake-pronouns">
                      <input
                        type="text" value={intake.pronouns}
                        onChange={(e) => setIntake({ ...intake, pronouns: e.target.value })}
                        className="form-input" data-testid="intake-pronouns-input"
                      />
                    </Field>
                  </div>
                  <Field label="Country / time zone (optional)" testid="intake-tz">
                    <input
                      type="text" value={intake.country_timezone}
                      onChange={(e) => setIntake({ ...intake, country_timezone: e.target.value })}
                      className="form-input" data-testid="intake-tz-input"
                    />
                  </Field>
                  <Field label="Anything you’d like us to know (optional)" testid="intake-message">
                    <textarea
                      rows={5} value={intake.message}
                      onChange={(e) => setIntake({ ...intake, message: e.target.value })}
                      className="form-input resize-none" data-testid="intake-message-input"
                    />
                  </Field>

                  <ConsentBox checked={intake.consent} onChange={(v) => setIntake({ ...intake, consent: v })} testid="intake-consent" />

                  <MarketingBox
                    checked={intake.marketing_consent}
                    onChange={(v) => setIntake({ ...intake, marketing_consent: v })}
                    testid="intake-marketing-consent"
                  />

                  <GdprNote />

                  <button
                    type="submit" disabled={intakeBusy} data-testid="intake-submit"
                    className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500 disabled:opacity-60"
                  >
                    {intakeBusy ? 'Sending…' : 'Request intake conversation'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* APPLICATION FORM */}
          {activeTab === 'apply' && (
            <div data-testid="application-form-wrapper" className="bg-brand-bone rounded-3xl p-8 md:p-12 border border-brand-brown/5">
              {appSent ? (
                <SuccessMessage testid="application-success" />
              ) : (
                <form onSubmit={submitApplication} className="space-y-10">
                  <FormSection title="Personal information">
                    <Field label="Full name" testid="app-name" required>
                      <input
                        type="text" required value={app.full_name}
                        onChange={(e) => setApp({ ...app, full_name: e.target.value })}
                        className="form-input" data-testid="app-name-input"
                      />
                    </Field>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Field label="Email address" testid="app-email" required>
                        <input
                          type="email" required value={app.email}
                          onChange={(e) => setApp({ ...app, email: e.target.value })}
                          className="form-input" data-testid="app-email-input"
                        />
                      </Field>
                      <Field label="Pronouns (optional)" testid="app-pronouns">
                        <input
                          type="text" value={app.pronouns}
                          onChange={(e) => setApp({ ...app, pronouns: e.target.value })}
                          className="form-input" data-testid="app-pronouns-input"
                        />
                      </Field>
                    </div>
                    <Field label="Country / time zone" testid="app-tz">
                      <input
                        type="text" value={app.country_timezone}
                        onChange={(e) => setApp({ ...app, country_timezone: e.target.value })}
                        className="form-input" data-testid="app-tz-input"
                      />
                    </Field>
                  </FormSection>

                  <FormSection title="Programme selection">
                    <p className="font-sans text-sm text-brand-brown-muted mb-4">Which Path are you interested in? (Select one or more)</p>
                    <div className="space-y-3">
                      {PATHS.map((p) => (
                        <label key={p.id} data-testid={`app-path-${p.id}`} className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={app.path_interest.includes(p.id)}
                            onChange={() => togglePath(p.id)}
                            className="mt-1.5 h-4 w-4 accent-brand-brown"
                          />
                          <span className="font-sans text-base text-brand-brown-light group-hover:text-brand-brown">
                            <span className="text-brand-gold uppercase tracking-[0.22em] text-[11px] mr-2">{p.label}</span>
                            {p.title}
                          </span>
                        </label>
                      ))}
                      <label className="flex items-start gap-3 cursor-pointer group" data-testid="app-path-full">
                        <input
                          type="checkbox"
                          checked={app.path_interest.includes('full-pathway')}
                          onChange={() => togglePath('full-pathway')}
                          className="mt-1.5 h-4 w-4 accent-brand-brown"
                        />
                        <span className="font-sans text-base text-brand-brown-light group-hover:text-brand-brown">
                          <span className="text-brand-gold uppercase tracking-[0.22em] text-[11px] mr-2">Full</span>
                          The full Nurture the Self Pathway (all three Paths)
                        </span>
                      </label>
                    </div>

                    <Field label="Preferred start" testid="app-start" className="mt-6">
                      <select
                        value={app.preferred_start}
                        onChange={(e) => setApp({ ...app, preferred_start: e.target.value })}
                        className="form-input" data-testid="app-start-input"
                      >
                        <option value="">— please choose —</option>
                        {START_DATES.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </Field>
                  </FormSection>

                  <FormSection title="Reflection">
                    <p className="font-sans text-sm text-brand-brown-muted mb-4">Take all the time you need. There are no right or wrong answers — only what feels true for you today.</p>
                    <Field label="What has drawn you to this programme?" testid="app-drawn">
                      <textarea rows={4} value={app.drawn_to_programme}
                        onChange={(e) => setApp({ ...app, drawn_to_programme: e.target.value })}
                        className="form-input resize-none" data-testid="app-drawn-input" />
                    </Field>
                    <Field label="How would you currently describe your relationship with food, your body and yourself?" testid="app-relationship">
                      <textarea rows={4} value={app.current_relationship}
                        onChange={(e) => setApp({ ...app, current_relationship: e.target.value })}
                        className="form-input resize-none" data-testid="app-relationship-input" />
                    </Field>
                    <Field label="What are you hoping to gain from this journey?" testid="app-hopes">
                      <textarea rows={4} value={app.hopes}
                        onChange={(e) => setApp({ ...app, hopes: e.target.value })}
                        className="form-input resize-none" data-testid="app-hopes-input" />
                    </Field>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Field label="Have you previously engaged in psychotherapy, counselling or eating disorder support?" testid="app-prior">
                        <select value={app.prior_therapy}
                          onChange={(e) => setApp({ ...app, prior_therapy: e.target.value })}
                          className="form-input" data-testid="app-prior-input">
                          <option value="">— please choose —</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      </Field>
                      <Field label="Are you currently receiving support from another healthcare professional?" testid="app-current">
                        <select value={app.current_support}
                          onChange={(e) => setApp({ ...app, current_support: e.target.value })}
                          className="form-input" data-testid="app-current-input">
                          <option value="">— please choose —</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      </Field>
                    </div>
                    <Field label="Is there anything else you would like us to know to help determine whether this programme is the right fit for you?" testid="app-notes">
                      <textarea rows={4} value={app.additional_notes}
                        onChange={(e) => setApp({ ...app, additional_notes: e.target.value })}
                        className="form-input resize-none" data-testid="app-notes-input" />
                    </Field>
                  </FormSection>

                  <ConsentBox checked={app.consent} onChange={(v) => setApp({ ...app, consent: v })} testid="app-consent" />

                  <MarketingBox
                    checked={app.marketing_consent}
                    onChange={(v) => setApp({ ...app, marketing_consent: v })}
                    testid="app-marketing-consent"
                  />

                  <GdprNote />

                  <button
                    type="submit" disabled={appBusy} data-testid="app-submit"
                    className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500 disabled:opacity-60"
                  >
                    {appBusy ? 'Sending with care…' : 'Send my application'}
                  </button>
                </form>
              )}
            </div>
          )}

          <p className="mt-10 text-center font-sans text-xs text-brand-brown-muted italic max-w-2xl mx-auto">
            Safe enough to explore. Structured enough to feel held. Spacious enough to unfold in one’s own time.
          </p>
        </div>
      </section>

      {/* Local styles for inputs */}
      <style>{`
        .form-input {
          width: 100%;
          background: #FDFBF7;
          border: 1px solid rgba(44,34,30,0.12);
          color: #2C221E;
          padding: 0.85rem 1rem;
          border-radius: 0.85rem;
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 0.95rem;
          transition: border-color 300ms ease, background 300ms ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #D4AF37;
          background: #FFFFFF;
        }
      `}</style>
    </div>
  );
}

function Field({ label, testid, required, children, className = '' }) {
  return (
    <label data-testid={`field-${testid}`} className={`block ${className}`}>
      <span className="block font-sans text-[12px] uppercase tracking-[0.22em] text-brand-brown-light/90 mb-2">
        {label}{required && <span className="text-brand-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

function FormSection({ title, children }) {
  return (
    <div>
      <h3 className="font-serif text-2xl text-brand-brown mb-6 pb-3 border-b border-brand-brown/10">{title}</h3>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

function ConsentBox({ checked, onChange, testid }) {
  return (
    <label data-testid={testid} className="flex items-start gap-3 cursor-pointer bg-brand-bg-soft rounded-2xl p-5 border border-brand-brown/5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1.5 h-4 w-4 accent-brand-brown"
        data-testid={`${testid}-input`}
      />
      <span className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">
        By submitting this form, you consent to us securely storing your information for the purposes of assessing suitability, communicating with you about Nurture the Self, and administering the programme. Your information will never be shared with third parties without your consent.
      </span>
    </label>
  );
}

function MarketingBox({ checked, onChange, testid }) {
  return (
    <label data-testid={testid} className="flex items-start gap-3 cursor-pointer bg-brand-bone rounded-2xl p-5 border border-brand-brown/5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1.5 h-4 w-4 accent-brand-brown"
        data-testid={`${testid}-input`}
      />
      <span className="font-sans text-sm text-brand-brown-light/95 leading-relaxed">
        I would also like to receive <em className="italic text-brand-brown">The Nurture the Self Journal</em>.
      </span>
    </label>
  );
}

function GdprNote() {
  return (
    <p data-testid="gdpr-note" className="font-sans text-xs text-brand-brown-muted italic leading-relaxed px-1">
      We will only use your information to communicate with you about Nurture the Self. You may unsubscribe from journal emails at any time.
    </p>
  );
}

function SuccessMessage({ testid }) {
  return (
    <div data-testid={testid} className="text-center py-10">
      <div className="mx-auto h-14 w-14 rounded-full bg-brand-sage flex items-center justify-center mb-6">
        <Check className="text-brand-brown" size={22} />
      </div>
      <h3 className="font-serif text-3xl text-brand-brown">Received with care.</h3>
      <p className="mt-4 font-sans text-base text-brand-brown-light/90 leading-relaxed max-w-xl mx-auto">
        Thank you for entrusting this with us. We will read your words gently and reach out to gently arrange a complimentary intake conversation. There is no need to do anything more right now.
      </p>
      <Link to="/" className="mt-8 inline-flex font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500">
        Return home
      </Link>
    </div>
  );
}
